import { EquipmentDropdownInputBare } from "#batteries-included-components/Dropdowns/EquipmentDropdownInputBare";
import { FacilityDropdownInputBare } from "#batteries-included-components/Dropdowns/FacilityDropdownInputBare";
import { FlowDropdownInputBare } from "#batteries-included-components/Dropdowns/FlowDropdownInputBare";
import { useListReportingGroups } from "#src/components/hooks/adapters/useReportingGroups";
import type {
  FilterBuilderCustomRenderInputProps,
  FilterBuilderFieldsType,
} from "@validereinc/common-components";
import {
  AssetType,
  AttributeDataType,
  type MeasurementTypeType,
} from "@validereinc/domain";
import React, { useMemo } from "react";

export const useBuildRecordToRecordDRVCFilterFields = ({
  measurementTypes = [],
}: {
  measurementTypes: MeasurementTypeType[];
}) => {
  const { data: reportingGroupsResponse, isLoading: isLoadingReportingGroups } =
    useListReportingGroups();

  const reportingGroupOptions = useMemo(() => {
    return reportingGroupsResponse?.data?.map((group) => group.id) ?? [];
  }, [reportingGroupsResponse?.data]);

  const assetTypeOptions = useMemo(() => {
    return Object.values(AssetType);
  }, []);

  const filterFields: FilterBuilderFieldsType = useMemo(
    () => ({
      reporting_group_id: {
        data_type: AttributeDataType.PICK_LIST,
        display_name: "Reporting Group",
        pick_list_options: reportingGroupOptions,
      },
      asset_type: {
        data_type: AttributeDataType.PICK_LIST,
        display_name: "Asset Type",
        pick_list_options: assetTypeOptions,
      },
      measurement_type: {
        data_type: AttributeDataType.PICK_LIST,
        display_name: "Measurement Type",
        pick_list_options: measurementTypes.map(({ id }) => id),
      },
      "facility.id": {
        data_type: "custom-pick-list",
        display_name: "Facility ID",
        renderInput: (props: FilterBuilderCustomRenderInputProps) => (
          <FacilityDropdownInputBare
            isInline
            isLabelShown={false}
            isMulti={props.isMulti}
            isDisabled={props.isDisabled}
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      "equipment.id": {
        data_type: "custom-pick-list",
        display_name: "Equipment ID",
        renderInput: (props: FilterBuilderCustomRenderInputProps) => (
          <EquipmentDropdownInputBare
            isInline
            isLabelShown={false}
            isMulti={props.isMulti}
            isDisabled={props.isDisabled}
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      "flow.id": {
        data_type: "custom-pick-list",
        display_name: "Flow ID",
        renderInput: (props: FilterBuilderCustomRenderInputProps) => (
          <FlowDropdownInputBare
            isInline
            isLabelShown={false}
            isMulti={props.isMulti}
            isDisabled={props.isDisabled}
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      "equipment.facility.id": {
        data_type: AttributeDataType.STRING,
        display_name: "Facility ID of Equipment",
      },
    }),
    [reportingGroupOptions, assetTypeOptions, measurementTypes]
  );

  const isLoading = isLoadingReportingGroups;

  return { filterFields, isLoading };
};
